import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { useSnackbar } from 'notistack';
import { CompaniesService } from 'api';
import type { CompanyFinancialHistory, FinancialHistoryPeriod } from 'api';
import {
  COMPANY_FINANCIAL_HISTORY_DEFAULT_ERROR_MESSAGE,
  COMPANY_FINANCIAL_HISTORY_QUERY_KEY,
} from 'common/constants/services/companies';

export type BasePeriod = 'quarter' | 'year';

export type { CompanyFinancialHistory, FinancialHistoryPeriod };

const getCompanyFinancialHistory = async (
  companyId: number,
  basePeriod: BasePeriod,
): Promise<CompanyFinancialHistory> =>
  CompaniesService.apiCompaniesFinancialHistoryRead(companyId, basePeriod) as Promise<CompanyFinancialHistory>;

const useGetCompanyFinancialHistory = (companyId: number | undefined, basePeriod: BasePeriod = 'quarter') => {
  const { enqueueSnackbar } = useSnackbar();

  const { data, isLoading, isError } = useQuery({
    enabled: !isNil(companyId),
    queryFn: () => getCompanyFinancialHistory(companyId as number, basePeriod),
    queryKey: [COMPANY_FINANCIAL_HISTORY_QUERY_KEY, companyId, basePeriod],
    retry: false,
  });

  useEffect(() => {
    if (isError) {
      enqueueSnackbar(COMPANY_FINANCIAL_HISTORY_DEFAULT_ERROR_MESSAGE, { variant: 'error' });
    }
  }, [isError, enqueueSnackbar]);

  return { data, isLoading, isError };
};

export default useGetCompanyFinancialHistory;
